import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format, parseISO } from "date-fns";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import {
  apiForLine,
  isLineReady,
  lineNotReadyReason,
  type LamLine,
  type LamShiftRollsRow,
} from "../../../api/lamLine";
import UnavailablePanel from "../../common/UnavailablePanel";
import QualityTimelineCard from "./QualityTimelineCard";
import Lam2RollsChart, {
  ROLL_COLORS,
  RollsLegend,
  fmtRolls as fmt,
  fmtShare,
} from "./Lam2RollsChart";

/**
 * Lamination roll counts, straight from the PLC's per-shift A/B/F tallies.
 *
 * The PLC enforces the count against the machine's own roll counter (operators
 * still press the button, but a miss alarms and an over-count is blocked), so
 * these are the authoritative numbers — no length-threshold classification.
 *
 * Both lines run the same PLC program, so both Rolls tabs are this one page.
 * A selection with no roll data (see `isLineReady`) gets the same layout
 * blurred, and never asks the API for it.
 */

const COLORS = ROLL_COLORS;

const SHIFT_COUNTS = [10, 30, 100];
const REFRESH_MS = 60_000;

const StatCard: React.FC<{
  label: string;
  value: string;
  color?: string;
  hint?: string;
}> = ({ label, value, color, hint }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ p: { xs: 1.5, md: 1.75 }, "&:last-child": { pb: 2 } }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {color && (
          <Box
            sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color }}
          />
        )}
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Lam2RollsDashboard: React.FC<{ line?: LamLine }> = ({
  line = "lam2",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [limit, setLimit] = useState<number>(30);
  const [rows, setRows] = useState<LamShiftRollsRow[]>([]);
  const ready = isLineReady(line);
  const [loading, setLoading] = useState(ready);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal, silent = false) => {
      if (!isLineReady(line)) return;
      if (!silent) setLoading(true);
      try {
        const data = await apiForLine(line).getShiftRolls({ limit, signal });
        setRows(data);
        setError(null);
      } catch (e: any) {
        if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          setError("Failed to load shift roll counts.");
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [limit, line],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    // Refresh in place so the in-progress shift keeps climbing.
    const id = setInterval(() => load(undefined, true), REFRESH_MS);
    return () => {
      clearInterval(id);
      ctrl.abort();
    };
  }, [load]);

  const totals = useMemo(() => {
    const t = rows.reduce(
      (acc, r) => {
        acc.a += r.a_rolls;
        acc.b += r.b_rolls;
        acc.f += r.f_rolls;
        return acc;
      },
      { a: 0, b: 0, f: 0 },
    );
    return { ...t, all: t.a + t.b + t.f };
  }, [rows]);

  const partial = useMemo(() => {
    const r = rows.find((x) => x.is_partial);
    if (!r) return null;
    let label = r.shift_start;
    try {
      label = format(parseISO(r.shift_start), "MMM d");
    } catch {
      /* keep raw */
    }
    return { label, name: r.shift_name };
  }, [rows]);

  // Quality on a clock — when the good and bad rolls actually ran. It blurs
  // itself for a line that is not ready.
  const timeline = (
    <Grid item xs={12}>
      <QualityTimelineCard line={line} />
    </Grid>
  );

  const body = (
    <>
      {/* Controls */}
      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <ToggleButtonGroup
            value={String(limit)}
            exclusive
            onChange={(_, v) => v && setLimit(Number(v))}
            size={isMobile ? "small" : "medium"}
          >
            {SHIFT_COUNTS.map((n) => (
              <ToggleButton key={n} value={String(n)}>
                {n} shifts
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Button
            variant="contained"
            onClick={() => load()}
            disabled={loading || !ready}
            size={isMobile ? "small" : "medium"}
          >
            {loading ? "Loading…" : "Refresh"}
          </Button>

          <Typography variant="caption" color="text.secondary">
            Counted by the PLC against the machine's own roll counter. The PLC
            keeps the last 100 shifts.
          </Typography>
        </Stack>
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Grid>
      )}

      {/* Totals */}
      <Grid item xs={12}>
        <LoadingOverlay show={loading} minHeight={90}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            {/* Each grade also as a share of every roll in the window. */}
            <StatCard
              label="A rolls"
              value={fmt(totals.a)}
              color={COLORS.a}
              hint={`${fmtShare(totals.a, totals.all)} of total`}
            />
            <StatCard
              label="B rolls"
              value={fmt(totals.b)}
              color={COLORS.b}
              hint={`${fmtShare(totals.b, totals.all)} of total`}
            />
            <StatCard
              label="F rolls"
              value={fmt(totals.f)}
              color={COLORS.f}
              hint={`${fmtShare(totals.f, totals.all)} of total`}
            />
            <StatCard
              label="Total rolls"
              value={fmt(totals.all)}
              hint={`${rows.length} shift${rows.length === 1 ? "" : "s"}`}
            />
          </Box>
        </LoadingOverlay>
      </Grid>

      {/* Stacked chart */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              flexWrap="wrap"
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Rolls by Shift</Typography>
              <RollsLegend />
            </Stack>

            <LoadingOverlay show={loading} minHeight={isMobile ? 260 : 380}>
              {rows.length === 0 && !loading && ready ? (
                <Typography variant="body2" color="text.secondary">
                  No shift roll counts recorded yet.
                </Typography>
              ) : (
                <Lam2RollsChart rows={rows} />
              )}
            </LoadingOverlay>

            {partial && (
              <MuiTooltip title="This shift is still running, so its bar is lower than it will finish at.">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  The faded bar ({partial.label} · {partial.name}) is the shift
                  in progress.
                </Typography>
              </MuiTooltip>
            )}
          </CardContent>
        </Card>
      </Grid>
    </>
  );

  return (
    <Grid
      container
      spacing={2}
      padding={{ xs: 1, sm: 2 }}
      maxWidth="xl"
      margin="auto"
    >
      {timeline}
      {ready ? (
        body
      ) : (
        // The rest of the page as an empty template under one blur, so the
        // tab keeps its shape without a number anyone could read off it.
        <Grid item xs={12}>
          <UnavailablePanel reason={lineNotReadyReason(line)}>
            <Grid container spacing={2}>
              {body}
            </Grid>
          </UnavailablePanel>
        </Grid>
      )}
    </Grid>
  );
};

export default Lam2RollsDashboard;
